import React, { Component } from 'react';
import Text from './Text';
import { NavLink } from 'react-router-dom';
import CL from '../images/command_line.png';
import PC from '../images/Pycharm.png';

const publicPath = "/";

const EnvDescription = <div id="Descript">


What do you need for this tutorial? In order:<br/>
- Python 2.7<br/>
- Python Libraries: Pandas, SKLearn, Matplotlib, Tensorflow, Keras<br/>
- The data from Coindesk<br/>
<br/>
Additional software that I used (not necessary):<br/>
- IPython (For better visualization) <br/>
- Pycharm (A Python IDE)<br/>
- Tensorflow with CUDA support (To run models faster, requires a GPU)<br/>
<br/>
If you need help to install any of the necessary software there are many tutorials online.
Python comes pre-installed with macOS and most of linux distributions, for windows check the instructions below.
Pandas, SKLearn, Matplotlib, Tensorflow, and Keras are python libraries.
Keras must be installed after Tensorflow, the rest in any order.
There shouldn’t be any problem installing any of them except Tensorflow if you want to use CUDA support.
In some operating systems installing the libraries could take more time, for example installing Pip 
(a software to install Python Libraries) is a little bit more complicated in windows than in macOS or Linux.<br/>
- <a href="https://www.howtogeek.com/197947/how-to-install-python-on-windows/">Python 2.7 in Windows</a><br/>
- <a href="https://programminghistorian.org/lessons/installing-python-modules-pip">Installing python libraries with Pip</a>
<br/><br/>

Depending on your hardware the training of the neural network could take several minutes.
In my case I have Tensorflow with GPU support (1060 GTX 6GB) but the model can still be trained using Tensorflow without GPU support because the data is not that heavyweight like image recognition.
<br/><br/>
<strong>Quick introduction to neural networks and LSTM </strong> (feel free to skip if you know this terms): <br/>
In the data science world, neural networks are systems used to predict or classify data, designed similar to biological brains.
They have different classes and one of them is RNNs (Recurrent Neural Networks). This class includes an internal state (memory) that makes the network more dynamic.
An LSTM network (Long Short-Term Memory) is an RNN where the cells “remember” values for time intervals which makes it suitable for time series predictions. 
<br/><br/>
<strong>Quick introduction to creating and testing a trading strategy </strong> (feel free to skip if you know this terms): <br/>
In the trading world, any strategy has to be tested using past data to check if the strategy would beat certain benchmark.
To do this we create a function in python that buys or sells the asset, in this case bitcoin, according to certain rules and at the end we can check how profitable the strategy was (final balance).
Is important to remember that the past performance of a strategy can help to take decisions, but will never guarantee future results.
<br/><br/>

Additional to these introductions there are a few concepts that you should check, they are not completely necessary to understand the tutorial but could help a lot if you decide
to implement any of these ideas by yourself, some will be explained briefly when used:<br/>
- <a href="https://en.wikipedia.org/wiki/Market_trend#Bull_market">Bull market, bear market</a> (Finance)<br/>
- <a href="https://www.investopedia.com/terms/v/volatility.asp">Volatility</a> (Finance)<br/>
- <a href="https://www.investopedia.com/terms/l/lookaheadbias.asp">Look-ahead bias</a> (Finance)<br/>
- <a href="https://en.wikipedia.org/wiki/Bitcoin">Bitcoin</a> (Cryptocurrency)<br/>
- <a href="https://www.programiz.com/python-programming/array#introduction">Array/List</a> (Python)<br/>
- <a href="http://www.dataschool.io/simple-guide-to-confusion-matrix-terminology/">Confusion Matrix</a> (Data Science)<br/>
- <a href="https://en.wikipedia.org/wiki/Loss_function">Loss function</a> (Neural networks)<br/>
- <a href="https://en.wikipedia.org/wiki/Gradient_descent">Gradient Descent Optimizer</a> (Neural networks)<br/>
- <a href="https://stackoverflow.com/questions/4752626/epoch-vs-iteration-when-training-neural-networks">Epoch, Batch Size</a> (Neural networks)
<br/><br/>

If you want to use a GPU to run models faster these links will help.
The steps to finish the installation can be long and very frustrating,
I recommend doing it if you know very well what you are doing, if you have plenty of free time to do it, or if you are planning to run a lot of models and need the GPU to do it faster:<br/>
- <a href="https://developer.nvidia.com/cuda-gpus">Check if your GPU supports CUDA</a><br/>
- <a href="https://developer.nvidia.com/cuda-80-ga2-download-archive">Download CUDA</a><br/>
- <a href="https://developer.nvidia.com/cudnn">Download CudNN</a><br/>
- <a href="http://docs.nvidia.com/cuda/cuda-installation-guide-linux/index.html">Install Cuda</a> (Linux)<br/>
- <a href="http://docs.nvidia.com/cuda/cuda-installation-guide-microsoft-windows/">Install Cuda</a> (Windows)<br/>
- <a href="https://www.tensorflow.org/install/install_linux#nvidia_requirements_to_run_tensorflow_with_gpu_support">Using Tensorflow with GPU</a> (Linux)<br/>
- <a href="https://www.tensorflow.org/install/install_windows#requirements_to_run_tensorflow_with_gpu_support">Using Tensorflow with GPU</a> (Windows)
<br/><br/>

Before starting to write code you need to decide if you are going to use a python project.
If you don't, you can still write code in the terminal after writing "python2.7" for example:<br/><br/>

<img src={CL} alt="CL"/>
<br/>

As you can see, I opened a new terminal window, wrote python2.7, pressed enter and ran the lines of code below without problem (only a warning that won't affect our work).<br/><br/>

If you prefer to use a python project and/or <a href="https://ipython.org/install.html">IPython</a> you
can install an IDE as <a href="https://www.jetbrains.com/pycharm/download/">PyCharm</a> (the one I will
use <a href="https://stackoverflow.com/questions/42562734/how-to-run-a-file-in-ipython-console-as-default-instead-of-terminal">integrated with IPython</a>).
If you install Pycharm, it will ask to create a project the first time you open it. 
Here's a screenshot of my window after running the code below:<br/><br/>

<img src={PC} alt="PC"/>
<br/>

One of the main advantages of PyCharm is opening multiples python consoles (except if you are using CUDA),
better visualization, syntax highlighters and using relative paths.
<br/><br/>

To check if everything is working with your enviroment open a python console and run this code: <br/>
<br/>
<code>
import os<br/>
import warnings<br/>
import numpy as np<br/>
import pandas as pd<br/>
from tensorflow import set_random_seed<br/>
from keras.layers.core import Dense, Activation<br/>
from keras.layers.recurrent import LSTM<br/>
from keras.models import Sequential<br/>
import matplotlib.pyplot as plt<br/>
from sklearn.preprocessing import StandardScaler<br/>
from datetime import datetime<br/>
from dateutil import parser<br/>
import random as rn<br/>
import json
</code>
<br/><br/>
<strong>Pandas</strong> is the library to work with dataframes. Each dataframe is a table with rows and columns.<br/>
<strong>Numpy</strong> is one of the most important libraries for doing math operations.<br/>
<strong>SKLearn</strong> is a library for data manipulation, preparation and modelling. We are going to use it to transform the data, in this case to scale it.<br/>
<strong>Matplotlib</strong> is the library that we are going to use to make charts and visualize the results.<br/>
<strong>Tensorflow</strong> is a library created by google to design and train neural networks. Will be imported to set the random seed.<br/>
<strong>Keras</strong> is the library that will help us to create the models in a more user-friendly way than using Tensorflow directly.<br/>
<strong>Datetime and Dateutil</strong> are libraries for manipulating dates.<br/>
<strong>Random</strong> is a library to create pseudo-random numbers.<br/>
<strong>Json</strong> is a library that will help us to save information about the models.<br/>
<br/>
If there is an error importing the libraries you are probably missing one and need to install it.
If there are no errors you are ready to check <NavLink to={`${publicPath}data`}>Part 2: Preparing The Data</NavLink>.

</div>;

class Enviroment extends Component {


  componentDidMount() {
    window.onscroll = function() {scrollFunction()};

    function scrollFunction() {
        if (document.body.scrollTop > 200 || document.documentElement.scrollTop > 200) {
            document.getElementById("myBtn").style.display = "block";
        } else {
            document.getElementById("myBtn").style.display = "none";
        }
    }

    function findPos(obj) {
      var curtop = 0;
      if (obj.offsetParent) {
          do {
              curtop += obj.offsetTop;
          } while (obj = obj.offsetParent);
      return [curtop];
      }
    }

    document.getElementById("myBtn").onclick = function() {
      const   scrollHeight = window.scrollY,
              scrollStep = Math.PI / ( 1000 / 15 ),
              cosParameter = scrollHeight / 2;
      var     scrollCount = 0,
              scrollMargin,
              scrollInterval = setInterval( function() {
                  if ( window.scrollY > 10 ) {
                      scrollCount = scrollCount + 1;  
                      scrollMargin = cosParameter - cosParameter * Math.cos( scrollCount * scrollStep );
                      window.scrollTo( 0, ( scrollHeight - scrollMargin ) );
                  } 
                  else clearInterval(scrollInterval); 
              }, 15 );
    }

    const scrollHeight = window.scrollY - findPos(document.getElementById("myNavbar"))[0],
    scrollStep = Math.PI / ( 1000 / 15 ),
    cosParameter = scrollHeight / 2;
    var scrollCount = 0,
    scrollMargin,
    scrollInterval = setInterval( function() {
        if ( window.scrollY - findPos(document.getElementById("myNavbar"))[0] > 10 ) {
            scrollCount = scrollCount + 1;  
            scrollMargin = cosParameter - cosParameter * Math.cos( scrollCount * scrollStep );
            window.scrollTo( 0, ( scrollHeight - scrollMargin ) );
        } 
        else clearInterval(scrollInterval); 
    }, 15 );
  }

  render() {
    return (
        <div id="Enviroment">
        <h1>Part 1: Setting Up The Enviroment</h1>
          <Text Searchable="Time" description={EnvDescription}/>
        </div>
    );
  }
}

export default Enviroment;



// WEBPACK FOOTER //
// ./src/components/Enviroment.js